import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import Logo from '../components/Logo';
import HamburgerMenuButton from '../components/HamburgerMenuButton';
import useDismissOnOutsideClick from '../hooks/useDismissOnOutsideClick';
import ProfilePaymentMethods from '../components/ProfilePaymentMethods';
import PhoneNumberInput from '../components/PhoneNumberInput';
import SmsConsentNotice from '../components/SmsConsentNotice';
import ProfileSubscription from '../components/ProfileSubscription';
import FreeTrialSignupWizard from '../components/FreeTrialSignupWizard';
import { USER_TYPES, formatUserTypeLabel, normalizeUserType } from '../constants/userTypes';
import { DeveloperMenuItem } from '../components/DeveloperNavigation';
import { optionalAuthHeaders } from '../utils/billingApi';
import {
    consumeStripeSetupRedirect,
    peekStripeSetupRedirect,
    withoutStripeSetupRedirectParams,
} from '../utils/stripeSetupConfirm';
import '../components/UserProfile.css';
import './ContractsList.css';
import { getContractApiUrl, isLocalDevHostname } from '../utils/apiUrls';
import { phoneDigits } from '../utils/phoneNumber';

const BACKEND_URL = getContractApiUrl();

const BROKERAGE_EMAIL_DOMAINS = [
    'berkshirehathawayhomeservices.com',
    'bhhs.com',
    'cbrealty.com',
    'century21.com',
    'coldwellbanker.com',
    'compass.com',
    'era.com',
    'experealty.com',
    'exprealty.com',
    'kw.com',
    'kellerwilliams.com',
    'remax.com',
    'sothebysrealty.com',
    'weichert.com',
];

function guessedPrimaryEmailKind(primaryEmail) {
    const domain = String(primaryEmail || '').toLowerCase().trim().split('@')[1] || '';
    const isBrokerage = BROKERAGE_EMAIL_DOMAINS.some(
        (parent) => domain === parent || domain.endsWith(`.${parent}`),
    );
    return isBrokerage ? 'brokerage' : 'business';
}

function aliasEmailLabel(primaryEmail, kind) {
    const effective = kind === 'business' || kind === 'brokerage'
        ? kind
        : guessedPrimaryEmailKind(primaryEmail);
    return effective === 'brokerage' ? 'Business email' : 'Brokerage email';
}

function isValidOptionalEmail(value) {
    const text = String(value || '').trim();
    if (!text) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

const Profile = () => {
    const { user, isAuthenticated, isLoading: authLoading, logout, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'account';
    const postLoginOnboardingRef = useRef(searchParams.get('onboarding') === '1');
    const previewSignupWizardRef = useRef(
        typeof window !== 'undefined'
        && isLocalDevHostname(window.location.hostname)
        && searchParams.get('preview_signup_wizard') === '1'
    );
    const setProfileTab = useCallback((tab) => {
        const next = { tab };
        ['referral_token', 'referral_send_id', 'onboarding',
            'setup_intent', 'setup_intent_client_secret', 'redirect_status',
            'preview_signup_wizard'].forEach((key) => {
            const value = searchParams.get(key);
            if (value) {
                next[key] = value;
            }
        });
        setSearchParams(next);
    }, [searchParams, setSearchParams]);
    const [isLoading, setIsLoading] = useState(true);
    const [profileLoadFailed, setProfileLoadFailed] = useState(false);
    const [savingSection, setSavingSection] = useState(null);
    const [isEditingPersonal, setIsEditingPersonal] = useState(false);
    const [isEditingCompany, setIsEditingCompany] = useState(false);
    const [message, setMessage] = useState(null);
    const [formData, setFormData] = useState({
        id: null, // Added to store user ID from backend
        name: '',
        email: '',
        alias_email: '',
        alias_email_label: 'Brokerage email',
        primary_email_kind: 'business',
        phone_number: '',
        user_type: 'real_estate_agent',
        license_number: '',
        licensed_state: '',
        company_name: '',
        company_address: '',
        company_license_number: '',
        payment_user_type: null,
    });
    const [showSignupWizard, setShowSignupWizard] = useState(false);
    const [errors, setErrors] = useState({});
    const [showDropdown, setShowDropdown] = useState(false);
    const userMenuRef = useRef(null);
    const dismissUserMenu = useCallback(() => {
        setShowDropdown(false);
    }, []);
    useDismissOnOutsideClick(userMenuRef, showDropdown, dismissUserMenu);
    const [canEditUserType, setCanEditUserType] = useState(false);
    const [wizardUserTypeLocked, setWizardUserTypeLocked] = useState(false);

    useEffect(() => {
        if (searchParams.get('onboarding') === '1') {
            postLoginOnboardingRef.current = true;
        }
        peekStripeSetupRedirect(searchParams);
    }, [searchParams]);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/');
            return;
        }

        if (isAuthenticated && user?.email) {
            fetchProfile();
        }
    }, [isAuthenticated, user, authLoading]);

    const fetchProfile = async () => {
        try {
            const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const response = await axios.get(`${BACKEND_URL}/user/profile?email=${user.email}`, { headers });
            if (response.data) {
                setProfileLoadFailed(false);
                setFormData({
                    ...formData,
                    ...response.data,
                    user_type: normalizeUserType(response.data.user_type) || response.data.user_type,
                    email: user.email, // Ensure login email is from Auth0
                    alias_email: response.data.alias_email || '',
                    primary_email_kind: response.data.primary_email_kind
                        || guessedPrimaryEmailKind(user.email),
                    alias_email_label: response.data.alias_email_label
                        || aliasEmailLabel(user.email, response.data.primary_email_kind),
                });
                const { is_super_admin, is_paying_subscriber, user_type_editable } = response.data;
                setCanEditUserType(
                    !!(is_super_admin || is_paying_subscriber || user_type_editable === true)
                );
                setWizardUserTypeLocked(user_type_editable === false);
                const ptype = response.data.payment_user_type;
                const needsCardSetup = (
                    ptype === 'free_trial'
                    && response.data.payment_method_status !== 'verified'
                );
                const profileOnlyWaitlist = response.data.profile_only_waitlist === true;
                // #208: pending-invite guests complete their profile only —
                // never the free-trial wizard (its card-setup step would ask
                // for billing, which approved-non-subscriber mode forbids).
                // After they save, is_invite_pending clears but they stay
                // nonbillable transaction guests (is_transaction_guest from
                // the backend), so return visits also skip the auto-wizard.
                // The Full Access CTA passes ?upgrade=1 — the one intentional
                // path into the trial/billing wizard for guests.
                const pendingInvite = response.data.is_pending_invite === true;
                const transactionGuest = response.data.is_transaction_guest === true;
                const upgradeIntent = searchParams.get('upgrade') === '1';
                const localPreviewWizard = (
                    previewSignupWizardRef.current
                    || (
                        typeof window !== 'undefined'
                        && isLocalDevHostname(window.location.hostname)
                        && searchParams.get('preview_signup_wizard') === '1'
                    )
                );
                const needsWizard = (
                    localPreviewWizard
                    || response.data.force_signup_wizard === true
                    || (
                        !profileOnlyWaitlist &&
                        !pendingInvite &&
                        !(transactionGuest && !upgradeIntent) &&
                        !response.data.is_super_admin
                        && (!ptype || needsCardSetup)
                    )
                );
                setShowSignupWizard(needsWizard);
                if (needsWizard) {
                    setProfileTab('account');
                } else if (activeTab !== 'payment' && peekStripeSetupRedirect(searchParams)) {
                    // Wizard and payment methods are both unmounted on this tab.
                    // Drop a completed Stripe redirect so it cannot replay later.
                    consumeStripeSetupRedirect();
                    setSearchParams(withoutStripeSetupRedirectParams(searchParams), { replace: true });
                }
                return response.data;
            }
        } catch (err) {
            console.error('Error fetching profile:', err);
            const status = err?.response?.status;
            // 404 means no User, waitlist seed, or invite seed. Auth0 email
            // alone must not unlock an editable profile.
            // Transient/server failures must not allow a later save of empty
            // formData.name to wipe an existing profile name.
            if (status === 404) {
                setProfileLoadFailed(true);
                setFormData(prev => ({ ...prev, email: user.email || prev.email }));
                setMessage('No KeyFlo profile exists for this account. Waitlist or invite access is required.');
            } else {
                setProfileLoadFailed(true);
                setFormData(prev => ({ ...prev, email: user.email || prev.email }));
                setMessage('Error loading profile. Please refresh before saving changes.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const PERSONAL_FIELDS = ['name', 'phone_number', 'license_number', 'licensed_state', 'user_type', 'alias_email', 'primary_email_kind'];
    const COMPANY_FIELDS = ['company_name', 'company_address', 'company_license_number'];

    const validatePersonal = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Name is required';
        if (phoneDigits(formData.phone_number).length !== 10) {
            newErrors.phone_number = 'Enter a 10-digit phone number';
        }

        if (['real_estate_agent', 'managing_broker', 'mortgage_broker', 'lender'].includes(formData.user_type)) {
            const license = (formData.license_number || '').trim().toLowerCase();
            const placeholderLicenses = new Set(['pending', 'n/a', 'na', 'none', 'tbd', 'unknown']);
            if (!license || placeholderLicenses.has(license)) {
                newErrors.license_number = 'License number is required';
            }
            if (!formData.licensed_state?.trim()) newErrors.licensed_state = 'State is required';
        }
        if (!isValidOptionalEmail(formData.alias_email)) {
            newErrors.alias_email = 'Enter a valid email address';
        } else if (
            formData.alias_email?.trim()
            && formData.alias_email.trim().toLowerCase() === formData.email.trim().toLowerCase()
        ) {
            newErrors.alias_email = 'Use a different address than your login email';
        }

        setErrors(prev => {
            const next = { ...prev };
            PERSONAL_FIELDS.forEach((field) => delete next[field]);
            return { ...next, ...newErrors };
        });
        return Object.keys(newErrors).length === 0;
    };

    const validateCompany = () => {
        const newErrors = {};
        if (!formData.company_name?.trim()) newErrors.company_name = 'Company name is required';

        if (
            formData.user_type !== 'buyer'
            && formData.user_type !== 'seller'
            && formData.user_type !== 'real_estate_agent'
            && !formData.company_address?.trim()
        ) {
            newErrors.company_address = 'Company address is required';
        }

        setErrors(prev => {
            const next = { ...prev };
            COMPANY_FIELDS.forEach((field) => delete next[field]);
            return { ...next, ...newErrors };
        });
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const next = { ...prev, [name]: value };
            if (name === 'primary_email_kind') {
                next.alias_email_label = aliasEmailLabel(prev.email, value);
            }
            return next;
        });
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleSave = async (section) => {
        if (profileLoadFailed) {
            setMessage('Error: Profile could not be loaded. Refresh the page before saving.');
            return;
        }
        const isPersonal = section === 'personal';
        if (isPersonal ? !validatePersonal() : !validateCompany()) return;

        setSavingSection(section);
        setMessage(null);

        try {
            const headers = await optionalAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const { data: saveResult } = await axios.post(
                `${BACKEND_URL}/user/profile`,
                formData,
                { headers },
            );
            setMessage(`${isPersonal ? 'Personal information' : 'Company information'} updated successfully!`);
            if (isPersonal) {
                setIsEditingPersonal(false);
            } else {
                setIsEditingCompany(false);
            }

            sessionStorage.removeItem(`keyflo_access_status_${user.email}`);
            sessionStorage.removeItem(`keyflo_access_data_${user.email}`);
            // Invitees become Users / ANS on save — refresh Gatekeeper so
            // "← Back" does not keep a stale WAITLIST decision.
            if (saveResult?.waitlist_pending !== true) {
                window.dispatchEvent(new CustomEvent('keyflo-access-changed'));
            }

            setTimeout(() => setMessage(null), 3000);
        } catch (err) {
            console.error('Error saving profile:', err);
            setMessage(err.response?.data?.error || 'Error saving profile. Please try again.');
        } finally {
            setSavingSection(null);
        }
    };

    const handleCancel = (section) => {
        fetchProfile();
        const fields = section === 'personal' ? PERSONAL_FIELDS : COMPANY_FIELDS;
        setErrors(prev => {
            const next = { ...prev };
            fields.forEach((field) => delete next[field]);
            return next;
        });
        if (section === 'personal') {
            setIsEditingPersonal(false);
        } else {
            setIsEditingCompany(false);
        }
    };

    const handleLogout = () => { sessionStorage.removeItem('keyflo_login_event_fired'); logout({ logoutParams: { returnTo: window.location.origin } }); };
    const handleDropdownToggle = () => setShowDropdown(!showDropdown);

    const renderAccountHeaderActions = (section) => {
        const isEditing = section === 'personal' ? isEditingPersonal : isEditingCompany;
        const isSaving = savingSection === section;

        return (
            <div className="profile-card-header-actions">
                {isEditing ? (
                    <>
                        <button
                            type="button"
                            className="profile-card-header-cancel-btn"
                            onClick={() => handleCancel(section)}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="profile-card-header-edit-btn"
                            onClick={() => handleSave(section)}
                            disabled={isSaving || profileLoadFailed}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        className="profile-card-header-edit-btn"
                        onClick={() => {
                            if (profileLoadFailed) {
                                setMessage('Error: Profile could not be loaded. Refresh the page before editing.');
                                return;
                            }
                            if (section === 'personal') {
                                setIsEditingPersonal(true);
                            } else {
                                setIsEditingCompany(true);
                            }
                        }}
                        disabled={profileLoadFailed}
                    >
                        Edit
                    </button>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (activeTab !== 'account') {
            setIsEditingPersonal(false);
            setIsEditingCompany(false);
            setErrors({});
        }
    }, [activeTab]);

    useEffect(() => {
        if (showSignupWizard && activeTab !== 'account') {
            setProfileTab('account');
        }
    }, [showSignupWizard, activeTab, setProfileTab]);

    if (authLoading || (isAuthenticated && isLoading)) return <LoadingSpinner />;

    const profileOnlyWaitlist = formData.profile_only_waitlist === true;
    const pendingInvite = formData.is_pending_invite === true;
    const profileTabs = (profileOnlyWaitlist || pendingInvite)
        ? [{ id: 'account', label: 'Account' }]
        : [
            { id: 'account', label: 'Account' },
            { id: 'subscription', label: 'Subscription' },
            { id: 'payment', label: 'Payment Method' },
        ];

    return (
        <div className="profile-page-container">
            <header className="app-header">
                <div className="header-content">
                    <div className="header-left">
                        <Logo size={40} variant="arrow" />
                        <span style={{
                            fontWeight: 700,
                            fontSize: '2.5rem',
                            fontFamily: "'Playfair Display', serif",
                            marginLeft: '12px',
                            color: '#2c3e50'
                        }}>Profile</span>
                    </div>
                    <div className="user-nav">
                        <button className="back-button-nav" onClick={() => navigate('/')}>← Back</button>
                        <div className="user-menu" ref={userMenuRef}>
                            <HamburgerMenuButton onClick={handleDropdownToggle} />
                            {showDropdown && (
                                <div className="dropdown-menu">
                                    <button className="menu-item" onClick={() => { setShowDropdown(false); navigate('/profile'); }}>Profile</button>
                                    <button className="menu-item" onClick={() => { setShowDropdown(false); navigate('/settings'); }}>Settings</button>
                                    {user?.email?.endsWith('@keyflo.ai') && (
                                        <DeveloperMenuItem
                                            userEmail={user?.email}
                                            onNavigate={(path) => {
                                                setShowDropdown(false);
                                                navigate(path);
                                            }}
                                        />
                                    )}
                                    <div className="divider"></div>
                                    <button className="menu-item" style={{ color: '#e74c3c' }} onClick={() => { setShowDropdown(false); handleLogout(); }}>Log Out</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="profile-main">
                {message && (
                    <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
                        {message}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                    {profileTabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setProfileTab(tab.id)}
                            style={{
                                padding: '10px 16px',
                                border: 'none',
                                background: 'none',
                                borderBottom: activeTab === tab.id ? '2px solid #7fb069' : '2px solid transparent',
                                fontWeight: activeTab === tab.id ? 700 : 500,
                                cursor: 'pointer',
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'subscription' && (
                    <div className="profile-tab-panel">
                        <ProfileSubscription
                            user={user}
                            backendUrl={BACKEND_URL}
                            paymentUserType={formData.payment_user_type}
                        />
                    </div>
                )}

                {activeTab === 'payment' && (
                    <ProfilePaymentMethods user={user} backendUrl={BACKEND_URL} />
                )}

                {activeTab === 'account' && (
                <div className="profile-grid">
                    {/* CARD 1: PERSONAL INFORMATION */}
                    <div className="profile-card">
                        <div className="profile-card-header">
                            <h2>Personal Information</h2>
                            {renderAccountHeaderActions('personal')}
                        </div>
                        <div className="profile-card-body">
                            <div className="form-group">
                                <label>User Type</label>
                                {canEditUserType && isEditingPersonal ? (
                                    <select
                                        name="user_type"
                                        value={formData.user_type}
                                        onChange={handleInputChange}
                                    >
                                        {USER_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className="readonly-field">
                                        {formatUserTypeLabel(formData.user_type)}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    disabled={!isEditingPersonal}
                                    className={errors.name ? 'error' : ''}
                                />
                                {errors.name && <span className="error-text">{errors.name}</span>}
                            </div>

                            {(formData.user_type === 'real_estate_agent' ||
                                formData.user_type === 'managing_broker' ||
                                formData.user_type === 'mortgage_broker' ||
                                formData.user_type === 'lender' ||
                                formData.user_type === 'transaction_coordinator') && (
                                    <div className="form-group">
                                        <label>
                                            {['mortgage_broker', 'lender'].includes(formData.user_type)
                                                ? 'NMLS Number *'
                                                : formData.user_type === 'transaction_coordinator'
                                                    ? 'License Number'
                                                    : 'License Number *'}
                                        </label>
                                        <input
                                            type="text"
                                            name="license_number"
                                            value={formData.license_number}
                                            onChange={handleInputChange}
                                            disabled={!isEditingPersonal}
                                            className={errors.license_number ? 'error' : ''}
                                        />
                                        {errors.license_number && <span className="error-text">{errors.license_number}</span>}
                                    </div>
                                )}

                            <div className="form-group">
                                <label>Email Address</label>
                                <input type="email" value={formData.email} disabled={true} />
                            </div>

                            <div className="form-group">
                                <label>This login email is a</label>
                                {isEditingPersonal ? (
                                    <select
                                        name="primary_email_kind"
                                        value={formData.primary_email_kind || guessedPrimaryEmailKind(formData.email)}
                                        onChange={handleInputChange}
                                    >
                                        <option value="business">Business email</option>
                                        <option value="brokerage">Brokerage email</option>
                                    </select>
                                ) : (
                                    <div className="readonly-field">
                                        {formData.primary_email_kind === 'brokerage'
                                            ? 'Brokerage email'
                                            : 'Business email'}
                                    </div>
                                )}
                                <span className="help-text">
                                    Change this if KeyFlo guessed wrong from the email domain.
                                </span>
                            </div>

                            <div className="form-group">
                                <label>
                                    {formData.alias_email_label
                                        || aliasEmailLabel(formData.email, formData.primary_email_kind)}
                                    {' '}
                                    (optional)
                                </label>
                                <input
                                    type="email"
                                    name="alias_email"
                                    value={formData.alias_email || ''}
                                    onChange={handleInputChange}
                                    disabled={!isEditingPersonal}
                                    placeholder={
                                        (formData.alias_email_label
                                            || aliasEmailLabel(formData.email, formData.primary_email_kind))
                                        === 'Business email'
                                            ? 'e.g. you@gmail.com'
                                            : 'e.g. you@exprealty.com'
                                    }
                                    className={errors.alias_email ? 'error' : ''}
                                />
                                <span className="help-text">
                                    Used so KeyFlo recognizes you when mail or SkySlope uses this address.
                                </span>
                                {errors.alias_email && <span className="error-text">{errors.alias_email}</span>}
                            </div>

                            <div className="form-group">
                                <label>Phone Number *</label>
                                <PhoneNumberInput
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    disabled={!isEditingPersonal}
                                    className={errors.phone_number ? 'error' : ''}
                                />
                                {errors.phone_number && <span className="error-text">{errors.phone_number}</span>}
                                <div data-sms-consent="profile">
                                    <SmsConsentNotice source="profile" />
                                </div>
                            </div>

                            {(formData.user_type !== 'buyer' && formData.user_type !== 'seller') && (
                                <div className="form-group">
                                    <label>Licensed State *</label>
                                    <input
                                        type="text"
                                        name="licensed_state"
                                        value={formData.licensed_state}
                                        onChange={handleInputChange}
                                        disabled={!isEditingPersonal}
                                        className={errors.licensed_state ? 'error' : ''}
                                    />
                                    {errors.licensed_state && <span className="error-text">{errors.licensed_state}</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CARD 2: COMPANY INFORMATION */}
                    <div className="profile-card">
                        <div className="profile-card-header">
                            <h2>Company Information</h2>
                            {renderAccountHeaderActions('company')}
                        </div>
                        <div className="profile-card-body">
                            <div className="form-group">
                                <label>Company Name *</label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleInputChange}
                                    disabled={!isEditingCompany}
                                    className={errors.company_name ? 'error' : ''}
                                />
                                {errors.company_name && <span className="error-text">{errors.company_name}</span>}
                            </div>

                            {(formData.user_type !== 'buyer' && formData.user_type !== 'seller') && (
                                <>
                                    <div className="form-group">
                                        <label>Company Address {formData.user_type !== 'real_estate_agent' ? '*' : '(Optional)'}</label>
                                        <textarea
                                            name="company_address"
                                            value={formData.company_address}
                                            onChange={handleInputChange}
                                            disabled={!isEditingCompany}
                                            className={errors.company_address ? 'error' : ''}
                                            rows="3"
                                        />
                                        {errors.company_address && <span className="error-text">{errors.company_address}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Company License Number</label>
                                        <input
                                            type="text"
                                            name="company_license_number"
                                            value={formData.company_license_number}
                                            onChange={handleInputChange}
                                            disabled={!isEditingCompany}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                </div>
                )}

            </main>

            {showSignupWizard && (
                <FreeTrialSignupWizard
                    user={user}
                    backendUrl={BACKEND_URL}
                    profileData={formData}
                    userTypeLocked={wizardUserTypeLocked}
                    onProfileSaved={(updated) => setFormData((prev) => ({ ...prev, ...updated }))}
                    onComplete={async () => {
                        postLoginOnboardingRef.current = false;
                        setShowSignupWizard(false);
                        sessionStorage.removeItem(`keyflo_access_status_${user.email}`);
                        sessionStorage.removeItem(`keyflo_access_data_${user.email}`);
                        try {
                            const accessResp = await axios.get(
                                `${BACKEND_URL}/user/access-status?email=${encodeURIComponent(user.email.toLowerCase().trim())}`,
                            );
                            window.dispatchEvent(new CustomEvent('keyflo-access-changed', {
                                detail: accessResp.data,
                            }));
                        } catch (_) {
                            window.dispatchEvent(new Event('keyflo-access-changed'));
                        }
                        const refreshed = await fetchProfile();
                        const waitlistOnly = (
                            refreshed?.profile_only_waitlist === true
                            || refreshed?.is_pending_invite === true
                        );
                        setProfileTab(waitlistOnly ? 'account' : 'subscription');
                    }}
                />
            )}
        </div>
    );
};

export default Profile;
